/* Copyright (c) 2020-present CloudIO Inc */

import { pageAtom } from 'designer/atoms';
import {
  AnalyzeContext,
  AnalyzeIssue,
  AnalyzeResult,
} from 'layout/Sidebar/panels/TreeView/AnalyzeUtils';
import { itemWithDefaultValues } from 'registry/Components';
import { isNull } from 'utils/isEmpty';
import { Item, RequiredItem } from '../../registry/Component.type';

export async function analyzeTextField({
  platform,
  appUid,
  pageId,
  itemId,
  mgr,
}: AnalyzeContext): Promise<AnalyzeResult> {
  const alerts: AnalyzeIssue[] = [];
  const autoFixes: (() => void)[] = [];
  const page = platform.getPage(appUid, pageId);
  if (!page) {
    return null;
  }
  const store = platform.getParentStore(appUid, pageId, itemId);
  const item = itemWithDefaultValues(page[itemId]) as RequiredItem<'TextField'>;
  const { viewAttribute, label, maxLength, required, insertable, updatable } =
    item.props;

  if (!store) {
    alerts.push({
      key: `incomplete-${itemId}`,
      showItemId: itemId,
      alert: `Incomplete ${mgr.name}. Must be wrapped inside a Data Store Reference!`,
      itemId,
    });
    return [alerts, autoFixes];
  }
  if (!viewAttribute) {
    return [alerts, autoFixes];
  }
  await store.initializeAttributes();
  const attr = store.getAttribute(viewAttribute);

  if (!attr) {
    return [alerts, autoFixes];
  }

  if (!isNull(attr.maxLength) && attr.maxLength !== maxLength) {
    const fix = () => {
      const _page = { ...platform.getPage(appUid, pageId) };
      const _item = _page[itemId] as Item<'TextField'>;
      const updatedItem: Item<'TextField'> = {
        ..._item,
        props: { ..._item.props, maxLength: attr.maxLength },
      };
      _page[_item.itemId] = updatedItem;
      platform.setter(pageAtom, _page, {
        params: { appUid, pageId },
      });
    };
    autoFixes.push(fix);
    alerts.push({
      key: `maxLength-${itemId}`,
      fix,
      showItemId: itemId,
      alert: `${mgr.name} [${label}]'s Max Length [${maxLength}] is not in sync with the Attribute's Max Length [${attr.maxLength}] in data source ${store.ds}!`,
      itemId,
      severity: 'warning',
    });
  }
  let readonly = store.isReadonly();
  if (!readonly) {
    readonly =
      insertable.type === 'boolean' &&
      !insertable.value &&
      updatable.type === 'boolean' &&
      !updatable.value;
  }
  if (!readonly && attr.allowBlank === 'N' && !required) {
    const fix = () => {
      const _page = { ...platform.getPage(appUid, pageId) };
      const _item = _page[itemId] as Item<'TextField'>;
      const updatedItem: Item<'TextField'> = {
        ..._item,
        props: { ..._item.props, required: true },
      };
      _page[_item.itemId] = updatedItem;
      platform.setter(pageAtom, _page, {
        params: { appUid, pageId },
      });
    };
    autoFixes.push(fix);
    alerts.push({
      key: `allowBlank-${itemId}`,
      fix,
      showItemId: itemId,
      alert: `${mgr.name} [${label}]'s is not marked as required, but the Attribute [${attr.attributeName}] in ${store.ds} is not optional!`,
      itemId,
      severity: 'warning',
    });
  }

  return [alerts, autoFixes];
}
